import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import * as roleService from "../../services/role.services";
import Breadcrums from "../../common/breadcrumbs";
import { useTranslation } from "react-i18next";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import { globalLoader, handleServerValidations } from "../../../../utils/commonfunction";
import * as Yup from "yup";
import CustomError from "../../../../utils/customError";

const RoleAdd = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [permissionList, setPermissionList] = useState([]);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState([]);
  const breadcrumbs = [
    { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
    { title: t("label_sub_admin"), url: "/admin/staff/list/1" },
    { title: t("label_role"), url: "/admin/role/list/1" },
    { title: t("link_add"), url: "" },
  ];

  useEffect(() => {
    roleService.PermissionsOptions().then((response) => {
      setPermissionList(response?.data || []);
    }).catch((error) => {
      console.log("error=====>", error);
    });
  }, []);

  const togglePermission = (id) => {
    setSelectedPermissionIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleGroup = (groupPermissions) => {
    const groupIds = groupPermissions.map((p) => p._id);
    const allSelected = groupIds.every((id) => selectedPermissionIds.includes(id));
    if (allSelected) {
      setSelectedPermissionIds((prev) => prev.filter((id) => !groupIds.includes(id)));
    } else {
      setSelectedPermissionIds((prev) => [...new Set([...prev, ...groupIds])]);
    }
  };

  const toggleAll = () => {
    const allIds = permissionList.map((p) => p._id);
    const allSelected = allIds.every((id) => selectedPermissionIds.includes(id));
    setSelectedPermissionIds(allSelected ? [] : allIds);
  };

  const groupedPermissions = permissionList.reduce((acc, p) => {
    const g = p.group_name || p.groupName || "";
    const key = (g && String(g).trim()) ? String(g).trim() : "Other";
    if (!acc[key]) acc[key] = [];
    acc[key].push(p);
    return acc;
  }, {});
  const groupKeys = Object.keys(groupedPermissions).sort();

  const allIds = permissionList.map((p) => p._id);
  const isAllSelected = allIds.length > 0 && allIds.every((id) => selectedPermissionIds.includes(id));

  const validationSchema = Yup.object().shape({
    title: Yup.string().required(t("label_title_error") || "Title is required").trim(),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: { title: "" },
    validateOnBlur: false,
    validationSchema,
    onSubmit: (values, { setSubmitting, resetForm }) => {
      globalLoader(true);
      setSubmitted(true);
      const payload = {
        title: (values.title || "").trim(),
        permissionIDs: selectedPermissionIds,
      };
      roleService.Add(payload).then((response) => {
        setSubmitting(false);
        if (response.success) {
          Swal.fire({ icon: "success", text: response.message, ...SWAL_SETTINGS });
          globalLoader(false);
          resetForm({ values: { title: "" } });
          navigate("/admin/role/list/1");
        } else {
          Swal.fire({ icon: "error", text: handleServerValidations(response), ...SWAL_SETTINGS });
          setSubmitted(false);
          globalLoader(false);
        }
      }).catch(() => {
        setSubmitting(false);
        setSubmitted(false);
        globalLoader(false);
      });
    },
  });

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <form onSubmit={formik.handleSubmit}>
        <div className="row row-sm">
          <div className="col-lg-12 col-md-12 animation_fade">
            <div className="card custom-card">
              <div className="card-body">
                <h6 className="main-content-label mb-3">{t("label_add_role")}</h6>
                <div className="row row-sm">

                  {/* Title */}
                  <div className="col-md-12 form-group">
                    <label htmlFor="title" className="text-left d-flex">
                      {t("label_title")}
                      <span className="requirestar">*</span>
                    </label>
                    <input
                      name="title"
                      id="title"
                      type="text"
                      placeholder={t("placeholder_enter_title") || "Enter the title"}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.title}
                      className="form-control"
                    />
                    <span className="text-danger d-flex text-left">
                      <CustomError name="title" form={formik} />
                    </span>
                  </div>

                  {/* Permissions */}
                  <div className="col-lg-12 form-group mt-3">
                    <h6 className="main-content-label mb-2">
                      {t("label_permissions")} <span className="requirestar">*</span>
                    </h6>

                    {/* Note */}
                    <h5 className="text-danger mb-3">{t("label_role_permission_note")}</h5>

                    {/* Check All */}
                    <div className="mb-3 d-flex align-items-center gap-2">
                      <span className="fw-bold">Check All</span>
                      <input
                        type="checkbox"
                        className="cp"
                        style={{ width: "18px", height: "18px", cursor: "pointer" }}
                        checked={isAllSelected}
                        onChange={toggleAll}
                      />
                    </div>

                    {!permissionList.length ? (
                      <p className="text-muted">{t("msg_no_records")}</p>
                    ) : (
                      groupKeys.map((groupName) => {
                        const groupPermissions = groupedPermissions[groupName];
                        const groupIds = groupPermissions.map((p) => p._id);
                        const isGroupSelected = groupIds.every((id) => selectedPermissionIds.includes(id));

                        return (
                          <div key={groupName} className="mb-4 pb-3 border-bottom">

                            {/* Group header - label pehle checkbox baad mein */}
                            <div className="d-flex align-items-center gap-2 mb-2">
                              <h6 className="text-primary mb-0 fw-bold">{groupName}</h6>
                              <input
                                type="checkbox"
                                className="cp"
                                style={{ width: "18px", height: "18px", cursor: "pointer" }}
                                checked={isGroupSelected}
                                onChange={() => toggleGroup(groupPermissions)}
                              />
                            </div>

                            {/* Individual permissions */}
                            <div className="d-flex flex-wrap gap-3 ps-2">
                              {groupPermissions.map((p) => (
                                <div key={p._id} className="d-flex align-items-center gap-2">
                                  <span>{p.title || p.short_slug || p._id}</span>
                                  <input
                                    type="checkbox"
                                    className="cp"
                                    style={{ width: "16px", height: "16px", cursor: "pointer" }}
                                    checked={selectedPermissionIds.includes(p._id)}
                                    onChange={() => togglePermission(p._id)}
                                  />
                                </div>
                              ))}
                            </div>

                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* Buttons */}
                  <div className="mt-5">
                    <button className="btn btn-info mr-2" type="submit" disabled={submitted}>
                      <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                      {t("btn_submit")}
                    </button>
                    <button className="btn ripple btn-secondary" type="button" disabled={submitted} onClick={() => navigate(-1)}>
                      <i className="ace-icon fa fa-times bigger-110 mx-1"></i>
                      {t("btn_cancel")}
                    </button>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default RoleAdd;